import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { ArrowRight, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

const categories = ['Toutes', 'Admissions', 'Partenariat', 'Événement', 'Innovation'];

const newsArticles = [
  {
    id: 1,
    slug: 'ouverture-inscriptions-2026-2027',
    title: "Ouverture des inscriptions pour l'ann\u00e9e acad\u00e9mique 2026\u20132027",
    date: "15 Mai 2026",
    excerpt: "ESTAD ouvre ses portes aux nouveaux candidats. D\u00e9couvrez nos programmes en T\u00e9l\u00e9coms, Informatique, Communication et \u00c9nergies.",
    tag: "Admissions",
    image: "/images/campus/doc-ceremony-01.jpg"
  },
  {
    id: 2,
    slug: 'partenariat-qh-india-2024',
    title: "Partenariat strat\u00e9gique avec QH India \u2014 D\u00e9cembre 2024",
    date: "20 D\u00e9c. 2025",
    excerpt: "Signature d'un accord de coop\u00e9ration acad\u00e9mique et scientifique avec QH India, renfor\u00e7ant le rayonnement international d'ESTAD.",
    tag: "Partenariat",
    image: "/images/campus/doc-event-03.jpg"
  },
  {
    id: 3,
    slug: 'journee-portes-ouvertes-2026',
    title: "Journ\u00e9e portes ouvertes \u2014 Innovation et Entrepreneuriat",
    date: "10 Mars 2026",
    excerpt: "Venez d\u00e9couvrir le CATI, nos laboratoires et rencontrer nos enseignants-chercheurs lors de cette journ\u00e9e d\u00e9di\u00e9e \u00e0 l'innovation.",
    tag: "\u00c9v\u00e9nement",
    image: "/images/campus/doc-gathering-02.jpg"
  }
];

export default function News() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('Toutes');

  const filteredArticles = activeCategory === 'Toutes' 
    ? newsArticles 
    : newsArticles.filter(a => a.tag === activeCategory);

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('news_page_title', 'Actualités & Événements')}
        description={t('news_page_subtitle', 'Restez informé de la vie académique et scientifique d’ESTAD.')}
        breadcrumbs={[{ label: t('nav_news', 'News'), href: '/news' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-12 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* Category Filter */}
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 pb-8">
            <Filter className="w-5 h-5 text-gold mr-2" />
            {categories.map((cat) => (
              <button 
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                  activeCategory === cat 
                    ? 'bg-navy text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-12">
            {filteredArticles.map((article) => (
              <div key={article.id} className="flex flex-col md:flex-row gap-8 bg-gray-50 p-6 rounded-sm border border-gray-100 hover:shadow-xl transition-all group">
                <div className="w-full md:w-64 h-48 overflow-hidden rounded-sm flex-shrink-0">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-gold text-navy text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">{article.tag}</span>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{article.date}</span>
                  </div>
                  <h3 className="text-2xl font-black text-navy group-hover:text-gold transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed line-clamp-2 font-medium">
                    {article.excerpt}
                  </p>
                  <Link to={`/news/${article.slug}`} className="inline-flex items-center gap-2 text-navy font-black underline hover:text-gold uppercase tracking-widest text-xs transition-colors group/link">
                    {t('common_read_more', 'Lire la suite')} <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
            
            {filteredArticles.length === 0 && (
              <div className="py-20 text-center text-gray-400 font-bold uppercase tracking-widest italic">
                Aucun article trouvé dans cette catégorie.
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="mt-12 pt-12 border-t border-gray-100 flex justify-center gap-4">
            <button className="w-12 h-12 rounded-full border border-gold bg-navy text-white flex items-center justify-center font-black shadow-lg transition-all">1</button>
            <button className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center font-black text-gray-400 hover:text-navy transition-all">2</button>
          </div>
        </div>
      </main>
    </div>
  );
}
